// Responsive image contract: D:/AI/site-queensbutler/docs/structure/04_詳細設計_ページ内容スキーマ.md §5
import type { ImgHTMLAttributes } from 'react'
import { getImageMetadata, imageBase } from '@/lib/images'

export type PictureProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt' | 'width' | 'height'> & {
  src: string
  alt: string
  width?: number
  height?: number
}

export function Picture({ src, alt, width, height, loading = 'eager', sizes = '100vw', ...imageProps }: PictureProps) {
  const metadata = getImageMetadata(src)
  if (!metadata) {
    if (!width || !height) {
      throw new Error(`Picture に未登録画像を渡す場合は width と height が必須です: ${src}`)
    }
    return (
      <picture data-image={imageBase(src)}>
        <img src={src} alt={alt} width={width} height={height} loading={loading} {...imageProps} />
      </picture>
    )
  }

  const base = imageBase(src)
  const variantsFor = (format: 'jpg' | 'png' | 'webp') => metadata.variants
    .filter((variant) => variant.format === format)
    .sort((left, right) => left.width - right.width)
  const webp = variantsFor('webp')
  const fallbackFormat = metadata.variants.some((variant) => variant.format === 'jpg') ? 'jpg' : 'png'
  const fallback = variantsFor(fallbackFormat)
  const largest = fallback.at(-1)
  if (!largest) throw new Error(`Picture の画像variantがありません: ${src}`)

  const srcSet = (variants: readonly { src: string; width: number }[]) => variants
    .map((variant) => `${variant.src} ${variant.width}w`)
    .join(', ')

  return (
    <picture data-image={base}>
      {webp.length > 0 && <source type="image/webp" srcSet={srcSet(webp)} sizes={sizes} />}
      <source type={`image/${fallbackFormat === 'jpg' ? 'jpeg' : 'png'}`} srcSet={srcSet(fallback)} sizes={sizes} />
      <img
        src={largest.src}
        srcSet={srcSet(fallback)}
        sizes={sizes}
        alt={alt}
        width={width ?? metadata.width}
        height={height ?? metadata.height}
        loading={loading}
        {...imageProps}
      />
    </picture>
  )
}
